if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId || 'quickview';
        this.sectionRoot = this.closest('[id^="shopify-section-"]') || document.getElementById(`shopify-section-${this.sectionId}`) || this.closest('product-info') || this;
        this.variantPickerId = this.variantPickerId || `product-variant-picker-variant-selects-${this.sectionId}`;

        this.denominationsErrorEl = this.sectionRoot?.querySelector('#gift-card-denomination-error');

        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.sectionId}`);
        this.fieldsWrapper = this.querySelector('.recipient-fields');
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.sectionId}`);
        this.emailInput = this.querySelector(`#Recipient-email-${this.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.sectionId}`);
        this.uniqueIdProperty = this.querySelector(`#Recipient-unique-id-${this.sectionId}`);

        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        this.defaultErrorHeader = this.errorMessage?.innerText;

        this.currentProductVariantId = this.dataset.productVariantId;
        this.hasInteracted = false;

        this.productForm = this.sectionRoot?.querySelector('product-form form[action*="/cart/add"]') || 
                          this.closest('product-form')?.querySelector('form[action*="/cart/add"]');

        const buttonSelector = 'product-form form[action*="/cart/add"] button[name="add"], product-form form[action*="/cart/add"] button[type="submit"], product-form form[action*="/cart/add"] button[name="buy"]';
        this.addButtons = Array.from((this.sectionRoot || document).querySelectorAll(buttonSelector));

        this.hideFields();
        this.disableInputFields();
        this.setGiftControl(false);

        this.onDenominationChange = this.handleDenominationChange.bind(this);
        this.onProductFormSubmit = this.handleProductFormSubmit.bind(this);
        this.onFieldInput = this.handleFieldInput.bind(this);
        this.onFieldBlur = this.handleFieldBlur.bind(this);
      }

      connectedCallback() {
        this._changeTarget = this.sectionRoot || this;
        this._changeTarget.addEventListener('change', this.onDenominationChange);

        if (this.productForm) {
          this.productForm.addEventListener('submit', this.onProductFormSubmit, true);
        }

        this.onAddButtonClick = (e) => {
          const btnForm = e.target?.form || e.target?.closest('form');
          if (!btnForm || btnForm !== this.productForm) return;

          if (this.canSubmit()) {
            this.prepareGiftCardPayloadForSubmit();
            return;
          }

          e.preventDefault();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          e.stopPropagation();

          if (this.isDenominationSelected()) {
            this.openRecipient({ announce: false });
          } else {
            if (this.denominationsErrorEl) {
              this.denominationsErrorEl.style.display = 'block';
              this.denominationsErrorEl.removeAttribute('hidden');
            }
          }

          const { errors } = this.validateRecipientFields();
          const showErrors = Object.keys(errors).length ? errors : { email: ['Recipient email is required'] };
          this.displayErrorMessage('Please enter recipient details', showErrors);
          this.focusFirstInvalid();
        };

        this.addButtons.forEach((btn) => btn.addEventListener('click', this.onAddButtonClick, true));

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();

            const shouldStayOpen = this.isOpen() || this.hasInteracted;
            if (shouldStayOpen) this.openRecipient({ announce: false });
            else this.closeRecipient({ announce: false });
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });

        this.priceUpdaterUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          const variant = event.data.variant;
          requestAnimationFrame(() => this.updateGiftcardPrice(variant));
        });

        this.inputFields().forEach((field) => {
          if (!field) return;
          field.addEventListener('input', this.onFieldInput);
          field.addEventListener('blur', this.onFieldBlur);
        });

        // Handle date input placeholder visibility for iOS
        if (this.sendonInput) {
          const toggleDatePlaceholder = () => {
            if (this.sendonInput.value) {
              this.sendonInput.classList.add('has-value');
            } else {
              this.sendonInput.classList.remove('has-value');
            }
          };

          // Check on load
          toggleDatePlaceholder();

          // Update on change
          this.sendonInput.addEventListener('change', toggleDatePlaceholder);
          this.sendonInput.addEventListener('input', toggleDatePlaceholder);
        }
      }

      disconnectedCallback() {
        if (this._changeTarget) {
          this._changeTarget.removeEventListener('change', this.onDenominationChange);
          this._changeTarget = null;
        }
        if (this.priceUpdaterUnsub) this.priceUpdaterUnsub();
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
        this.addButtons?.forEach((btn) => btn.removeEventListener('click', this.onAddButtonClick, true));
      }

      updateGiftcardPrice(variant) {
        if (!variant?.id || !this.sectionRoot) return;

        const checked = this.sectionRoot.querySelector('input[name*="denomination" i]:checked');
        if (!checked) return;

        const updatedPrice = checked.dataset.variantPrice;
        if (!updatedPrice) return;

        // Batch DOM updates for better performance
        const priceElements = this.sectionRoot.querySelectorAll('.price__regular.price-giftcard__range');
        priceElements.forEach(el => el.textContent = updatedPrice);
      }

      handleDenominationChange(e) {
        if (this.denominationsErrorEl) {
          this.denominationsErrorEl.style.display = 'none';
          this.denominationsErrorEl.setAttribute('hidden', '');
        }

        const target = e.target;
        if (!target || target.nodeName !== 'INPUT') return;

        const matchesDenomination = /denomination/i.test(target.name || '') || /^id$/i.test(target.name || '');
        if (!matchesDenomination) return;

        const picker = document.getElementById(this.variantPickerId);
        if (!picker?.contains(target)) return;

        this.openRecipient();
      }

      canSubmit() {
        const enabled = this.hiddenControlField && this.hiddenControlField.value === 'if_present';
        if (!enabled || !this.isOpen()) return false;

        const { valid } = this.validateRecipientFields();
        return valid;
      }

      isDenominationSelected() {
        const denominationOptions = this.sectionRoot.querySelectorAll('input[name*="denomination" i], input[name="id"]');
        return Array.from(denominationOptions).some(el => el.checked);
      }

      handleProductFormSubmit(event) {
        if (this.canSubmit()) {
          this.prepareGiftCardPayloadForSubmit();
          return;
        }

        event.preventDefault();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        event.stopPropagation();

        this.openRecipient({ announce: false });

        const { errors } = this.validateRecipientFields();
        const showErrors = Object.keys(errors).length ? errors : { email: ['Recipient email is required'] };
        this.displayErrorMessage('Please enter recipient details', showErrors);
        this.focusFirstInvalid();
      }

      validateRecipientFields() {
        const errors = {};
        const email = this.emailInput?.value?.trim() || '';
        const name = this.nameInput?.value?.trim() || '';
        const message = this.messageInput?.value?.trim() || '';
        const sendOn = this.sendonInput?.value?.trim() || '';

        if (!email) {
          errors.email = ['Recipient email is required'];
        } else if (!this.isValidEmail(email)) {
          errors.email = ['Enter a valid email address'];
        }

        if (name && !this.isValidText(name, 'name')) {
          errors.name = ['Name can only contain letters, numbers, spaces, and basic punctuation (.,!?-\'&())'];
        }

        if (message && !this.isValidText(message, 'message')) {
          errors.message = ['Message can only contain letters, numbers, spaces, and basic punctuation (.,!?-\'&())'];
        }

        if (sendOn && !this.isValidFutureDate(sendOn)) {
          errors.sendon = ['Please select a future date'];
        }

        return { valid: Object.keys(errors).length === 0, errors };
      }

      validateField(fieldName) {
        const field = this[`${fieldName}Input`];
        if (!field) return { valid: true, errors: {} };

        const value = field.value?.trim() || '';
        const errors = {};

        if (fieldName === 'email') {
          if (!value) {
            errors.email = ['Recipient email is required'];
          } else if (!this.isValidEmail(value)) {
            errors.email = ['Enter a valid email address'];
          }
        } else if (fieldName === 'name') {
          if (value && !this.isValidText(value, 'name')) {
            errors.name = ['Name can only contain letters, numbers, spaces, and basic punctuation (.,!?-\'&())'];
          }
        } else if (fieldName === 'message') {
          if (value && !this.isValidText(value, 'message')) {
            errors.message = ['Message can only contain letters, numbers, spaces, and basic punctuation (.,!?-\'&())'];
          }
        } else if (fieldName === 'sendon') {
          if (value && !this.isValidFutureDate(value)) {
            errors.sendon = ['Please select a future date'];
          }
        }

        return { valid: Object.keys(errors).length === 0, errors };
      }

      handleFieldInput(event) {
        const field = event.target;
        if (!field || !this.isOpen()) return;

        const fieldName = this.getFieldName(field);
        if (!fieldName) return;

        this.clearFieldError(fieldName);
      }

      handleFieldBlur(event) {
        const field = event.target;
        if (!field || !this.isOpen()) return;

        const fieldName = this.getFieldName(field);
        if (!fieldName) return;

        const { valid, errors } = this.validateField(fieldName);
        if (!valid) {
          this.displayFieldError(fieldName, errors[fieldName]);
        } else {
          this.clearFieldError(fieldName);
        }
      }

      getFieldName(field) {
        const fieldMap = new Map([
          [this.emailInput, 'email'],
          [this.nameInput, 'name'],
          [this.messageInput, 'message'],
          [this.sendonInput, 'sendon']
        ]);
        return fieldMap.get(field) || null;
      }

      renderFieldErrors(errorsObj) {
        if (!errorsObj || typeof errorsObj !== 'object') return;
        Object.entries(errorsObj).forEach(([key, messages]) => {
          this.displayFieldError(key, messages);
        });
      }

      displayFieldError(fieldName, errorMessages) {
        const field = this[`${fieldName}Input`];
        if (!field || !errorMessages?.length) return;

        const errorIdMap = {
          'sendon': 'send_on',
          'email': 'email',
          'name': 'name',
          'message': 'message'
        };
        const errorFieldName = errorIdMap[fieldName] || fieldName;
        const errorMessageId = `RecipientForm-${errorFieldName}-error-${this.sectionId}`;
        const errorMessageElement = this.querySelector(`#${errorMessageId}`);
        const errorTextElement = errorMessageElement?.querySelector('.error-message');

        if (errorTextElement) {
          errorTextElement.textContent = `${errorMessages.join(', ')}.`;
          errorMessageElement.classList.remove('hidden');
        }

        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', errorMessageId);
      }

      clearFieldError(fieldName) {
        const field = this[`${fieldName}Input`];
        if (!field) return;

        const errorIdMap = {
          'sendon': 'send_on',
          'email': 'email',
          'name': 'name',
          'message': 'message'
        };
        const errorFieldName = errorIdMap[fieldName] || fieldName;
        const errorMessageId = `RecipientForm-${errorFieldName}-error-${this.sectionId}`;
        const errorMessageElement = this.querySelector(`#${errorMessageId}`);

        if (errorMessageElement) {
          errorMessageElement.classList.add('hidden');
          const errorTextElement = errorMessageElement.querySelector('.error-message');
          if (errorTextElement) errorTextElement.textContent = '';
        }

        field.setAttribute('aria-invalid', 'false');
        field.removeAttribute('aria-describedby');
      }

      isValidEmail(email) {
        if (typeof email !== 'string' || email.length > 254) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }

      isValidFutureDate(dateString) {
        if (!dateString) return false;

        const [y, m, d] = dateString.split('-').map(Number);
        const selected = new Date(y, m - 1, d);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selected.setHours(0, 0, 0, 0);

        return selected >= today;
      }

      isValidText(text, fieldName) {
        if (!text || typeof text !== 'string') return true;

        const allowedPattern = /^[a-zA-Z0-9\s.,!?\-'&()]+$/;

        if (!allowedPattern.test(text)) {
          return false;
        }

        if (fieldName === 'name') {
          const trimmedText = text.trim();
          if (trimmedText.length === 0) return true;
          if (/[.,!?\-'&()]{2,}/.test(trimmedText)) {
            return false;
          }

          if (/^[.,!?\-'&()]|[.,!?\-'&()]$/.test(trimmedText)) {
            return false;
          }
        }
        return true;
      }

      focusFirstInvalid() {
        const firstInvalid = this.querySelector('.recipient-fields [aria-invalid="true"], .recipient-fields [required]:invalid') ||
                            this.emailInput || this.nameInput;

        try {
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          const isDateInput = firstInvalid?.type === 'date';

          if (isMobile && isDateInput) {
            const fieldName = this.getFieldName(firstInvalid);
            if (fieldName) {
              const errorIdMap = {
                'sendon': 'send_on',
                'email': 'email',
                'name': 'name',
                'message': 'message'
              };
              const errorFieldName = errorIdMap[fieldName] || fieldName;
              const errorMessageId = `RecipientForm-${errorFieldName}-error-${this.sectionId}`;
              const errorElement = this.querySelector(`#${errorMessageId}`);
              errorElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          } else {
            firstInvalid?.focus();
          }
        } catch (error) {
          console.warn('RecipientForm: Could not focus element', error);
        }
      }

      isOpen() {
        return this.fieldsWrapper && !this.fieldsWrapper.hasAttribute('hidden');
      }

      showFields() {
        this.fieldsWrapper?.removeAttribute('hidden');
      }

      hideFields() {
        this.fieldsWrapper?.setAttribute('hidden', '');
      }

      openRecipient({ announce = true } = {}) {
        this.showFields();
        this.enableInputFields();
        this.setGiftControl(true);
        this.clearErrorMessage();
        this.hasInteracted = true;

        this.setRequiredAttributes(true);

        if (announce && this.recipientFieldsLiveRegion) {
          this.recipientFieldsLiveRegion.innerText =
            window.accessibilityStrings?.recipientFormExpanded || 'Recipient form expanded';
        }
      }

      closeRecipient({ announce = true } = {}) {
        this.hideFields();
        this.clearInputFields();
        this.disableInputFields();
        this.setGiftControl(false);
        this.clearErrorMessage();

        this.setRequiredAttributes(false);

        if (announce && this.recipientFieldsLiveRegion) {
          this.recipientFieldsLiveRegion.innerText =
            window.accessibilityStrings?.recipientFormCollapsed || 'Recipient form collapsed';
        }
      }

      setGiftControl(enabled) {
        if (!this.hiddenControlField) return;
        this.hiddenControlField.disabled = !enabled;
        this.hiddenControlField.value = enabled ? 'if_present' : '';

        if (enabled && this.uniqueIdProperty) {
          this.uniqueIdProperty.disabled = false;
          this.uniqueIdProperty.value = this.generateUniqueId();
        } else if (this.uniqueIdProperty) {
          this.uniqueIdProperty.disabled = true;
          this.uniqueIdProperty.value = '';
        }
      }

      resetRecipientForm() {
        this.closeRecipient();
      }

      inputFields() {
        if (!this._inputFieldsCache) {
          this._inputFieldsCache = [this.emailInput, this.nameInput, this.messageInput, this.sendonInput].filter(Boolean);
        }
        return this._inputFieldsCache;
      }

      disableableFields() {
        if (!this._disableableFieldsCache) {
          this._disableableFieldsCache = [...this.inputFields(), this.offsetProperty, this.uniqueIdProperty].filter(Boolean);
        }
        return this._disableableFieldsCache;
      }

      generateUniqueId() {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 10000);
        return `gc_${timestamp}_${random}`;
      }

      setRequiredAttributes(required) {
        if (this.emailInput) this.emailInput.toggleAttribute('required', !!required);
      }

      clearInputFields() {
        this.inputFields().forEach(field => field.value = '');
      }

      enableInputFields() {
        this.disableableFields().forEach(field => field.disabled = false);
      }

      disableInputFields() {
        this.disableableFields().forEach(field => field.disabled = true);
      }

      displayErrorMessage(_title, body) {
        if (typeof body === 'object' && body) {
          this.renderFieldErrors(body);
        } else if (body) {
          this.displayFieldError('email', [String(body)]);
        }
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        ['email', 'name', 'message', 'sendon'].forEach((f) => this.clearFieldError(f));
      }

      // Create/update a hidden input inside the <form> mirroring the source field's name/value
      mirrorFieldToForm(form, sourceEl) {
        if (!form || !sourceEl || !sourceEl.name) return;
        const name = sourceEl.name;
        let hidden = form.querySelector(`input[type="hidden"][name="${CSS.escape(name)}"]`);
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = name;
          form.appendChild(hidden);
        }
        hidden.value = sourceEl.value || '';
      }

      ensureHiddenPropsInForm() {
        const form = this.productForm;
        if (!form) return;

        // control flag
        if (this.hiddenControlField) {
          this.hiddenControlField.disabled = false;
          if (!form.contains(this.hiddenControlField)) form.appendChild(this.hiddenControlField);
        }

        // unique id (keeps items separate)
        if (this.uniqueIdProperty) {
          this.uniqueIdProperty.disabled = false;
          this.uniqueIdProperty.value = this.generateUniqueId();
          if (!form.contains(this.uniqueIdProperty)) form.appendChild(this.uniqueIdProperty);
        }

        // timezone offset
        if (this.offsetProperty) {
          this.offsetProperty.disabled = false;
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
          if (!form.contains(this.offsetProperty)) form.appendChild(this.offsetProperty);
        }
      }

      // Mirror all visible recipient fields into the real <form>
      syncRecipientFieldsIntoForm() {
        const form = this.productForm;
        if (!form) return;
        if (this.emailInput)   this.mirrorFieldToForm(form, this.emailInput);
        if (this.nameInput)    this.mirrorFieldToForm(form, this.nameInput);
        if (this.messageInput) this.mirrorFieldToForm(form, this.messageInput);
        if (this.sendonInput)  this.mirrorFieldToForm(form, this.sendonInput);
      }

      // prepare everything before submit
      prepareGiftCardPayloadForSubmit() {
        this.setGiftControl(true);
        this.ensureHiddenPropsInForm();
        this.syncRecipientFieldsIntoForm();
      }

      // re-bind the component to a new DOM context (used in quickview modal for gc)
      updateContext({ sectionRoot, productForm, addButtons, variantPickerId } = {}) {
        if (this.addButtons && this.onAddButtonClick) {
          this.addButtons.forEach(btn =>
            btn.removeEventListener('click', this.onAddButtonClick, true)
          );
        }
        if (this.productForm && this.onProductFormSubmit) {
          this.productForm.removeEventListener('submit', this.onProductFormSubmit, true);
        }

        if (this._changeTarget) {
          this._changeTarget.removeEventListener('change', this.onDenominationChange);
        }
        this._changeTarget = sectionRoot || this;
        this._changeTarget.addEventListener('change', this.onDenominationChange);

        if (sectionRoot) this.sectionRoot = sectionRoot;
        if (productForm) this.productForm = productForm;
        if (Array.isArray(addButtons)) this.addButtons = addButtons;
        if (variantPickerId) this.variantPickerId = variantPickerId;

        if (!this.variantPickerId) {
          const picker = this.sectionRoot?.querySelector('variant-selects');
          if (picker) this.variantPickerId = picker.id || picker.getAttribute('id');
        }

        let emailById = this.querySelector('[id^="Recipient-email-"]') ||
                        this.sectionRoot?.querySelector?.('[id^="Recipient-email-"]');
        if (emailById) {
          const m = emailById.id.match(/Recipient-email-(.+)$/);
          if (m) this.sectionId = m[1];
        } else if (!this.sectionId) {
          this.sectionId = 'quickview';
        }

        this.denominationsErrorEl      = this.sectionRoot?.querySelector('#gift-card-denomination-error') || null;
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.sectionId}`) || null;
        this.fieldsWrapper             = this.querySelector('.recipient-fields') || null;
        this.hiddenControlField        = this.querySelector(`#Recipient-control-${this.sectionId}`) || null;
        this.emailInput                = this.querySelector(`#Recipient-email-${this.sectionId}`) || null;
        this.nameInput                 = this.querySelector(`#Recipient-name-${this.sectionId}`) || null;
        this.messageInput              = this.querySelector(`#Recipient-message-${this.sectionId}`) || null;
        this.sendonInput               = this.querySelector(`#Recipient-send-on-${this.sectionId}`) || null;
        this.offsetProperty            = this.querySelector(`#Recipient-timezone-offset-${this.sectionId}`) || null;
        this.uniqueIdProperty          = this.querySelector(`#Recipient-unique-id-${this.sectionId}`) || null;

        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        this._inputFieldsCache = null;
        this._disableableFieldsCache = null;

        this.hideFields();
        this.disableInputFields();
        this.setGiftControl(false);
        this.clearErrorMessage();

        if (this.productForm) {
          this.productForm.addEventListener('submit', this.onProductFormSubmit, true);
        }
        if (this.addButtons && this.onAddButtonClick) {
          this.addButtons.forEach(btn =>
            btn.addEventListener('click', this.onAddButtonClick, true)
          );
        }

        const denomSelected = this.sectionRoot?.querySelector(
          'input[name*="denomination" i]:checked, input[name="id"]:checked'
        );
        if (denomSelected) {
          this.openRecipient({ announce: false });
        }
      }
    }
  );
}
